import { useEffect, useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent } from '../components/ui/card';
import { GraduationCap, ListChecks, Users, Scale, FolderArchive, BellRing, Settings2, ScrollText, FileBarChart, ClipboardCheck, CalendarClock, UploadCloud, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { cpdAPI, errMsg } from '../lib/cpdRegisterAPI';
import { CPDDashboardTab } from '../components/cpd/CPDDashboardTab';
import { CPDActivitiesTab } from '../components/cpd/CPDActivitiesTab';
import { CPDActivityDialog } from '../components/cpd/CPDActivityDialog';
import { CPDActivityDrawer } from '../components/cpd/CPDActivityDrawer';
import { CPDTeamTab } from '../components/cpd/CPDTeamTab';
import { CPDFrameworksTab } from '../components/cpd/CPDFrameworksTab';
import { CPDEvidenceTab } from '../components/cpd/CPDEvidenceTab';
import { CPDAlertsTab } from '../components/cpd/CPDAlertsTab';
import { CPDSettingsTab } from '../components/cpd/CPDSettingsTab';
import { CPDAuditTab } from '../components/cpd/CPDAuditTab';
import { CPDReportsTab } from '../components/cpd/CPDReportsTab';
import { CPDImportTab } from '../components/cpd/CPDImportTab';
import { CPDMyTraining } from '../components/cpd/CPDMyTraining';
import { CPDTrainingAdmin } from '../components/cpd/CPDTrainingAdmin';
import { CPDAssistant } from '../components/cpd/CPDAssistant';
import { toast } from 'sonner';

const T = ({ v, icon: I, children }) => <TabsTrigger value={v} data-testid={`cpd-tab-${v}`}><I className="w-4 h-4 mr-2" />{children}</TabsTrigger>;

export default function CPDRegister() {
  const [me, setMe] = useState(null);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get('tab') || 'my');
  const [dlg, setDlg] = useState(null);
  const [drawer, setDrawer] = useState(null);
  const [rk, setRk] = useState(0);
  const [assist, setAssist] = useState(false);
  useEffect(() => { cpdAPI.me().then(setMe).catch((e) => setErr(errMsg(e))); }, []);
  const bump = () => setRk((x) => x + 1);
  if (err) return <div className="p-6"><Card><CardContent className="py-10 text-center text-muted-foreground" data-testid="cpd-no-access">{err}</CardContent></Card></div>;
  if (!me) return <p className="p-6 text-sm text-muted-foreground">Loading…</p>;
  const lead = ['admin', 'manager', 'auditor'].includes(me.tier);
  const own = me.tier !== 'auditor';
  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="cpd-register-page">
      <div className="flex items-start justify-between gap-4 flex-wrap"><div>
        <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>CPD / CPE Register</h1>
        <p className="text-muted-foreground mt-1">Continuing professional development across TPB, CPA, IPA and your own frameworks – calculated on the server against versioned rules.</p>
      </div><Button variant="outline" className="rounded-full" onClick={() => setAssist(true)} data-testid="cpd-assistant-btn"><Sparkles className="w-4 h-4 mr-1" />CPD Q&amp;A</Button></div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto">
          {own && <T v="my" icon={GraduationCap}>My CPD</T>}
          {own && <T v="activities" icon={ListChecks}>My activities</T>}
          {lead && <T v="team" icon={Users}>Team CPD</T>}
          {['admin', 'manager'].includes(me.tier) && <T v="approvals" icon={ClipboardCheck}>Approvals</T>}
          {lead && <T v="all" icon={ListChecks}>All activities</T>}
          <T v="training" icon={CalendarClock}>Training</T>
          <T v="evidence" icon={FolderArchive}>Evidence</T>
          <T v="alerts" icon={BellRing}>Alerts</T>
          <T v="frameworks" icon={Scale}>Frameworks</T>
          {['admin', 'auditor'].includes(me.tier) && <T v="audit" icon={ScrollText}>Audit</T>}
          <T v="reports" icon={FileBarChart}>Reports</T>
          {me.tier === 'admin' && <T v="import" icon={UploadCloud}>Import</T>}
          {me.tier === 'admin' && <T v="settings" icon={Settings2}>Settings</T>}
        </TabsList>
        {own && <TabsContent value="my"><CPDDashboardTab me={me} refreshKey={rk} onAdd={() => setDlg({})} /></TabsContent>}
        {own && <TabsContent value="activities"><CPDActivitiesTab me={me} userId={me.user_id} refreshKey={rk} onOpen={setDrawer} onAdd={() => setDlg({})} /></TabsContent>}
        {lead && <TabsContent value="team"><CPDTeamTab me={me} refreshKey={rk} onOpen={setDrawer} onAddFor={(uid) => setDlg({ userId: uid })} /></TabsContent>}
        {lead && <TabsContent value="approvals"><CPDActivitiesTab me={me} status="submitted" showStaff refreshKey={rk} onOpen={setDrawer} /></TabsContent>}
        {lead && <TabsContent value="all"><CPDActivitiesTab me={me} showStaff refreshKey={rk} onOpen={setDrawer} /></TabsContent>}
        <TabsContent value="evidence"><CPDEvidenceTab onOpen={setDrawer} showStaff={lead} /></TabsContent>
        <TabsContent value="alerts"><CPDAlertsTab me={me} /></TabsContent>
        <TabsContent value="frameworks"><CPDFrameworksTab me={me} /></TabsContent>
        <TabsContent value="audit"><CPDAuditTab /></TabsContent>
        <TabsContent value="reports"><CPDReportsTab me={me} /></TabsContent>
        <TabsContent value="training"><div className="space-y-6">{own && <CPDMyTraining me={me} refreshKey={rk} onDraft={(id) => { setTab('activities'); setDrawer(id); bump(); }} />}
          {lead && <CPDTrainingAdmin me={me} />}</div></TabsContent>
        {me.tier === 'admin' && <TabsContent value="import"><CPDImportTab /></TabsContent>}
        {me.tier === 'admin' && <TabsContent value="settings"><CPDSettingsTab me={me} onChanged={() => { bump(); cpdAPI.me().then(setMe).catch((e) => toast.error(errMsg(e))); }} /></TabsContent>}
      </Tabs>
      <CPDActivityDialog open={!!dlg} onOpenChange={(v) => !v && setDlg(null)} me={me} userId={dlg?.userId} activity={dlg?.activity} onSaved={bump} />
      <CPDAssistant open={assist} onOpenChange={setAssist} me={me} />
      <CPDActivityDrawer id={drawer} me={me} onClose={() => setDrawer(null)} onChanged={bump} onEdit={(a) => { setDrawer(null); setDlg({ activity: a }); }} />
    </div>
  );
}
